import { Box, FormControl, ListSubheader, MenuItem, Select, Typography } from "@mui/material"
import { CheckCircleFilledRounded } from "@mui-symbols-material/w300"
import dayjs from "dayjs"
import { type FC, useMemo } from "react"
import { useFormContext } from "react-hook-form"

import { FieldGroup, FieldRow, Form, FormField } from "@/components/Forms"
import { SELECT_STYLING_PROPS } from "@/constants/forms"
import {
  formatAnyModeTransportIncoterms,
  formatSeaAndInlandWaterwayIncoterms,
  formattedPricingTiers,
} from "@/constants/pricingTiers"
import type { Product } from "@/graphql/codegen/graphql"
import { useMergedIncotermsLocations } from "@/hooks/useMergedIncotermsLocations"
import { isActiveIncotermsLocation } from "@/utils/locations"
import { formatLocationAddress } from "@/utils/utils"

interface FormValues {
  pricingTiersToCreate: {
    numberOfUnits: number
    pricePerUnit: number
    unitOfMeasurement: {
      name: string
    }
  }[]
  effectiveDate: string
  expirationDate?: string
}

interface PriceBreakdownProps {
  formData: FormValues
}

const PriceBreakdown: FC<PriceBreakdownProps> = ({ formData }) => (
  <Box className='space-y-4'>
    <Box className='mb-2' display='flex' alignItems='center'>
      <Typography
        variant='body1'
        fontWeight={700}
        className='leading-6 tracking-[0.15px] text-gray-700'
        display='flex'
        alignItems='center'
      >
        1. Price Breakdown
        <CheckCircleFilledRounded className='ml-1 text-success-500' />
      </Typography>
    </Box>
    <Box className='px-6'>
      <Box className='mb-3 w-1/2'>
        <Box className='flex justify-between'>
          <Typography variant='body2' className='font-bold text-gray-700'>
            Quantity
          </Typography>
          <Typography variant='body2' className='w-1/2 font-bold text-gray-700'>
            Price/UoM
          </Typography>
        </Box>
        {formData.pricingTiersToCreate.map(
          ({ numberOfUnits, pricePerUnit, unitOfMeasurement }, index) =>
            numberOfUnits &&
            pricePerUnit &&
            unitOfMeasurement.name && (
              <Box key={index} className='flex justify-between'>
                <Typography variant='body2' className='text-gray-700'>
                  {numberOfUnits}
                </Typography>
                <Typography variant='body2' className='w-1/2 text-gray-700'>
                  {`$${pricePerUnit} / ${unitOfMeasurement.name}`}
                </Typography>
              </Box>
            )
        )}
      </Box>
      <p className='mb-3 py-0 text-sm text-gray-900'>
        Effective Date: {dayjs(formData.effectiveDate).format("MM/DD/YYYY")}
      </p>
      <p className='py-0 text-sm text-gray-900'>
        Expiration Date: {formData?.expirationDate ? dayjs(formData.expirationDate).format("MM/DD/YYYY") : "-"}
      </p>
    </Box>
  </Box>
)

interface AddShippingTermsProps {
  product: Product
}

export const AddShippingTerms: FC<AddShippingTermsProps> = ({ product }) => {
  const { getValues } = useFormContext<FormValues>()
  const companyLocationAssociations = product.company?.locationsAssociations ?? []
  const { locations: mergedLocations } = useMergedIncotermsLocations(companyLocationAssociations)
  const formData = useMemo(() => getValues(), [getValues])

  return (
    <Form className='gap-6'>
      <PriceBreakdown formData={formData} />
      <FieldGroup legend='2. Shipping Terms'>
        <FieldRow>
          <FormField
            name='freightTerms'
            label='Freight Terms'
            required
            slotProps={{
              label: {
                className: "text-gray-800 text-[12px] font-normal",
              },
            }}
            render={({ field, fieldState }) => (
              <FormControl>
                <Select {...field} {...SELECT_STYLING_PROPS} error={!!fieldState.error}>
                  {formattedPricingTiers.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </FieldRow>
        <FieldRow>
          <FormField
            name='incoterms'
            label='Incoterms'
            required
            slotProps={{
              label: {
                className: "text-gray-800 text-[12px] font-normal",
              },
            }}
            render={({ field, fieldState }) => (
              <FormControl>
                <Select {...field} {...SELECT_STYLING_PROPS} error={!!fieldState.error}>
                  <ListSubheader className='text-base font-bold text-black'>Any mode of Transport:</ListSubheader>
                  {formatAnyModeTransportIncoterms.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                  <ListSubheader className='text-base font-bold text-black'>
                    Sea and Inland Waterway Transport:
                  </ListSubheader>
                  {formatSeaAndInlandWaterwayIncoterms.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </FieldRow>
        <FieldRow>
          <FormField
            name='incotermsLocationId'
            label='Incoterms Location'
            required
            slotProps={{
              label: {
                className: "text-gray-800 text-[12px] font-normal",
              },
            }}
            render={({ field, fieldState }) => (
              <FormControl>
                <Select {...field} {...SELECT_STYLING_PROPS} error={!!fieldState.error}>
                  {mergedLocations.map((location) => (
                    <MenuItem
                      key={location.locationId}
                      value={location.locationId}
                      disabled={!isActiveIncotermsLocation(location)}
                    >
                      {formatLocationAddress(location)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </FieldRow>
      </FieldGroup>
    </Form>
  )
}
